// Currency conversion: fetches USD-based rates from backend (cached) once per page
// and exposes a helper to convert any amount between two currency codes.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getCurrencyByCountry } from '@/utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'b2b_fx_rates_v1';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours client-side

let inflight = null;

export const fetchRates = async () => {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (cached && cached.fetched_at && Date.now() - cached.fetched_at < CACHE_TTL_MS) {
      return cached;
    }
  } catch (e) {
    // ignore
  }
  if (inflight) return inflight;
  inflight = axios
    .get(`${API_URL}/api/fx/rates`)
    .then((res) => {
      const payload = { base: res.data.base || 'USD', rates: res.data.rates || {}, fetched_at: Date.now() };
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(payload)); } catch (e) {}
      return payload;
    })
    .finally(() => { inflight = null; });
  return inflight;
};

export const convert = (amount, fromCode, toCode, rates) => {
  if (!amount || !fromCode || !toCode || !rates) return null;
  if (fromCode === toCode) return parseFloat(amount);
  const fromRate = rates[fromCode];
  const toRate = rates[toCode];
  if (!fromRate || !toRate) return null;
  // Rates are USD-based: 1 USD = fromRate FROM, 1 USD = toRate TO
  const usdAmount = parseFloat(amount) / fromRate;
  return usdAmount * toRate;
};

export const useFxRates = () => {
  const [rates, setRates] = useState(null);
  useEffect(() => {
    let mounted = true;
    fetchRates()
      .then((p) => { if (mounted) setRates(p?.rates || null); })
      .catch((err) => { console.warn('FX rates fetch failed:', err); });
    return () => { mounted = false; };
  }, []);
  return rates;
};

// Convenience: format a listing amount in viewer's local currency when they differ.
// Returns string like "≈ ₹83" or empty when not applicable.
export const formatConverted = (amount, listingCountry, viewerCountry, rates) => {
  if (!amount || !rates) return '';
  const from = getCurrencyByCountry(listingCountry);
  const to = getCurrencyByCountry(viewerCountry);
  if (!from || !to || from.code === to.code) return '';
  const converted = convert(amount, from.code, to.code, rates);
  if (converted == null || isNaN(converted)) return '';
  const rounded = converted >= 100 ? Math.round(converted) : Math.round(converted * 100) / 100;
  try {
    const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: to.code, maximumFractionDigits: rounded >= 100 ? 0 : 2 });
    return `≈ ${fmt.format(rounded)}`;
  } catch (err) {
    console.warn('FX Intl format failed:', err);
    return `≈ ${to.symbol}${rounded.toLocaleString()}`;
  }
};
